import { useId } from 'react';

/**
 * Star rating (SRS §4.3, §4.4). Stars are filled with the brand sky-500 — a fill
 * use, so the brand hue is allowed here even though its ink variant is used for text.
 */
export function Rating({
  value,
  count,
  size = 16,
  showCount = true,
  compact = false,
}: {
  value: number | null;
  count?: number;
  size?: number;
  showCount?: boolean;
  compact?: boolean;
}): JSX.Element {
  const rounded = Math.round((value ?? 0) * 2) / 2;
  if (compact) {
    return (
      <span
        className="inline-flex shrink-0 items-center gap-1 text-[11px] leading-4 text-neutral"
        role="img"
        aria-label={
          value
            ? `Rated ${value} out of 5${count ? ` from ${count} reviews` : ''}`
            : 'No ratings yet'
        }
      >
        <span aria-hidden>
          <Star size={12} fill="full" />
        </span>
        <span aria-hidden>{value?.toFixed(1) ?? '—'}</span>
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1"
      role="img"
      aria-label={value ? `Rated ${value} out of 5` : 'No ratings yet'}
    >
      <span className="inline-flex" aria-hidden>
        {[0, 1, 2, 3, 4].map((i) => {
          const fill = rounded - i >= 1 ? 'full' : rounded - i === 0.5 ? 'half' : 'empty';
          return <Star key={i} size={size} fill={fill} />;
        })}
      </span>
      {showCount && (
        <span className="text-xs text-neutral">
          {value ? value.toFixed(1) : 'New'}
          {count !== undefined && count > 0 ? ` (${count})` : ''}
        </span>
      )}
    </span>
  );
}

function Star({ size, fill }: { size: number; fill: 'full' | 'half' | 'empty' }): JSX.Element {
  const id = useId();
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" focusable="false">
      {fill === 'half' && (
        <defs>
          <linearGradient id={id}>
            <stop offset="50%" stopColor="var(--color-sky-500)" />
            <stop offset="50%" stopColor="var(--color-frost)" />
          </linearGradient>
        </defs>
      )}
      <path
        d="M10 1.5l2.6 5.3 5.9.9-4.3 4.2 1 5.8L10 15.9 4.8 18.9l1-5.8L1.5 8.9l5.9-.9z"
        fill={
          fill === 'full'
            ? 'var(--color-sky-500)'
            : fill === 'half'
              ? `url(#${id})`
              : 'var(--color-frost)'
        }
      />
    </svg>
  );
}
